import { config } from '@iheartjane/config';
import { Storage } from '@iheartjane/utils';

import { getFingerprintId, sendAnalytics } from '@jane/shared/data-access';
import {
  generateFallbackJdid,
  getCookie,
  getLocalStorageWithTTL,
  jdidIsNano,
} from '@jane/shared/util';

import { request } from './utils/request';

export const fpKey = 'j_eyeD';
export const jdidKey = 'jdid';

export const bootstrapUrl = () => `${config.apiPath}/bootstrap`;

// Handle marketplace mode
//
// There are a few situations you can be in here.
//
// You have no jdid in localStorage or cookies.
//   We need to make a request to set the cookie and then extract the jdid.
// You have jdid in localStorage but not in a cookie.
//   We skip the request here BUT later a whoami request is made in marketplace mode that will set the cookie value for jdid so we should be fine as long as we don't overwrite the localStorage value with an empty cookie value in this case.
// You have jdid in the cookie but not in localStorage in which case we could pull it from the cookie directly but we instead make a request to the bootstrap endpoint to ensure the cookie value is set.
//   We make a request to the bootstrap endpoint to ensure the cookie value is set and then pull it from the cookie and set it in localStorage.
// You have jdid in both localStorage and the cookie but they are different values.
//   Its been like this for a while, at this point we're just aiming to fix the initialized state for someone that has no jdid in localstorage
//
async function handleMarketplace(documentCookie = document.cookie) {
  if (!Storage.get<string>(jdidKey)) {
    let cookieJdid = getCookie(jdidKey, documentCookie);
    if (cookieJdid) {
      Storage.set(jdidKey, cookieJdid);
      sendAnalytics({ source: 'cookie', jdid: cookieJdid });
      return;
    }

    await request(bootstrapUrl(), { method: 'GET' });
    cookieJdid = getCookie(jdidKey, document.cookie);
    if (cookieJdid) {
      Storage.set(jdidKey, cookieJdid);
      sendAnalytics({ source: 'cookie', jdid: cookieJdid });
      return;
    }

    const fallbackJdid = generateFallbackJdid();
    Storage.set(jdidKey, fallbackJdid);
    sendAnalytics({
      source: 'cookie-fallback',
      jdid: fallbackJdid,
      fallbackId: fallbackJdid,
    });
  }
}

// Handle case when JDID is already available in storage
async function handleExistingJdid(
  jdid: string,
  cachedFingerprintId: string | null
) {
  Storage.set(jdidKey, jdid);
  console.debug('JDID was available in cache');
  sendAnalytics({ source: 'cache', jdid });

  // Try to refresh fingerprint if we have a nanoid JDID
  if (jdidIsNano(jdid) && !cachedFingerprintId) {
    const newJdid = await getFingerprintId(jdid);
    if (newJdid) {
      console.debug('Refreshed cache for FPID');
      sendAnalytics({ source: 'stale-refresh', jdid, fpId: newJdid });
    }
  }
}

// Handle case when JDID is not available in storage
async function handleMissingJdid(cachedFingerprintId: string | null) {
  if (cachedFingerprintId) {
    console.debug('Set JDID to cached FPID');
    Storage.set(jdidKey, cachedFingerprintId);
    sendAnalytics({ source: 'cached-fpid', jdid: cachedFingerprintId });
    return;
  }

  const newJdid = await getFingerprintId();
  if (newJdid) {
    console.debug('Set JDID to new FPID');
    Storage.set(jdidKey, newJdid);
    sendAnalytics({ source: 'new-fpid', jdid: newJdid });
    return;
  }

  const fallbackJdid = generateFallbackJdid();
  console.debug('Set JDID to fallback');
  Storage.set(jdidKey, fallbackJdid);
  sendAnalytics({
    source: 'fallback',
    jdid: fallbackJdid,
    fallbackId: fallbackJdid,
  });
}

interface BootstrapOptions {
  documentCookie?: string;
  isMarketplace?: boolean;
  jdidOverride?: string | null;
}

export async function bootstrap({
  documentCookie = document.cookie,
  isMarketplace = false,
  jdidOverride = null,
}: BootstrapOptions = {}) {
  if (Storage.get('user_session')) {
    // clear out old auth storage
    Storage.remove('user_session');
  }

  if (Storage.get('store-snapshot')) {
    // don't use legacy local cart persistence
    Storage.remove('store-snapshot');
  }

  // Fall back to a 5 second ready window in case a page doesn't mark as ready
  setTimeout(function () {
    window.prerenderReady = true;
  }, 5000);

  window.BUILD_VERSION = config.buildVersion?.substring(0, 7) || '';

  Storage.remove('fpId');

  // Main bootstrap logic
  if (isMarketplace) {
    await handleMarketplace(documentCookie);
  } else if (jdidOverride) {
    Storage.set(jdidKey, jdidOverride);
  } else {
    const availableJdid = Storage.get<string>(jdidKey);
    const cachedFingerprintId = getLocalStorageWithTTL(fpKey);
    const isValidJdid =
      availableJdid &&
      availableJdid !== 'undefined' &&
      availableJdid !== 'null';

    if (isValidJdid) {
      await handleExistingJdid(availableJdid, cachedFingerprintId);
    } else {
      await handleMissingJdid(cachedFingerprintId);
    }
  }

  // If JDID is still not set, set it to a fallback value.
  if (!Storage.get<string>(jdidKey)) {
    const fallbackJdid = generateFallbackJdid();
    Storage.set(jdidKey, fallbackJdid);
    sendAnalytics({
      source: 'final-fallback',
      jdid: fallbackJdid,
      fallbackId: fallbackJdid,
    });
  }
}
